import React from "react";
import { statusMeta } from "../lib/statusMeta";

interface LifecycleBarProps {
  status: string;
  /**
   * "compact" (default): segments + inline labels, fits inline in
   * lists. The historical layout.
   *
   * "detail": segments + an explicit current-stage caption below.
   * Used in PropertyModal at the very top, where it serves as the
   * user's orientation anchor (#audit-5: "the pipeline graphic
   * should be the first thing visible in the panel").
   */
  variant?: "compact" | "detail";
}

// Static segment definitions for the 4-stage lifecycle progress bar.
// Color per segment is the canonical color of the first status at
// that stage (NOD=blue, Notice Filed=amber, Auction=red, Resolved=gray).
// All consumers' statuses are mapped to one of these stages via
// statusMeta(status).lifecycleStage.
const STEPS = [
  { key: "notice_of_default", label: "NOD", color: statusMeta("notice_of_default").color },
  { key: "notice_filed", label: "Notice Filed", color: statusMeta("notice_filed").color },
  { key: "auction_scheduled", label: "Auction", color: statusMeta("auction_scheduled").color },
  { key: "resolved", label: "Resolved", color: statusMeta("sold").color },
] as const;

const LifecycleBar: React.FC<LifecycleBarProps> = ({ status, variant = "compact" }) => {
  // Stage index comes from the shared status registry — adding a new
  // backend status (e.g. `auction_passed`) only requires updating
  // STATUS_META in lib/statusMeta.ts; this component picks it up.
  const meta = statusMeta(status);
  const activeIndex = meta.lifecycleStage;
  const isDetail = variant === "detail";

  return (
    <div className={isDetail ? "lifecycle-bar-v2 lifecycle-bar-v2--detail" : "lifecycle-bar-v2"}>
      <div className="lifecycle-bar-v2__row">
        {STEPS.map((step, i) => {
          const isFilled = i <= activeIndex;
          const isCurrent = i === activeIndex;
          return (
            <div
              key={step.key}
              className={[
                "lifecycle-step",
                isFilled ? "lifecycle-step--filled" : "lifecycle-step--future",
                isCurrent ? "lifecycle-step--current" : "",
                i === 0 ? "lifecycle-step--first" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              style={isFilled ? { backgroundColor: step.color } : undefined}
              title={isCurrent ? meta.desc : undefined}
            >
              {step.label}
            </div>
          );
        })}
      </div>
      {isDetail && meta.desc && (
        <div className="lifecycle-bar-v2__caption">
          <strong style={{ color: meta.color }}>{meta.label}</strong>
          {" — "}
          {meta.desc}
        </div>
      )}
    </div>
  );
};

export default LifecycleBar;
